import { z } from 'zod';
import { propertyFilterSchema } from './aiAudience';
import { DimensionType, MetricType } from './field';
import { JoinType } from './relation/enums';

export const customAdditionalMetricSchema = z.object({
    label: z
        .string()
        .optional()
        .describe('Optional display label for the metric'),
    type: z
        .nativeEnum(MetricType)
        .describe('Type of the metric (e.g., count, sum, average)'),
    description: z
        .string()
        .describe('Optional description explaining what the metric measures'),
    table: z.string().describe('Name of the table this metric is based on'),
    baseDimensionName: z
        .string()
        .describe('Name of the dimension this metric is based on'),
    name: z.string().describe('Name of the metric'),
    filters: z
        .array(propertyFilterSchema)
        .optional()
        .describe('Optional array of filter rules to apply to the metric'),
    percentile: z
        .number()
        .optional()
        .describe('Optional percentile value for percentile-based metrics'),
});

export type CustomAdditionalMetricSchemaType = z.infer<
    typeof customAdditionalMetricSchema
>;

export const customMetricSchema = z
    .object({
        customAdditionalMetric: customAdditionalMetricSchema,
        destinationTable: z
            .string()
            .describe('Name of the table this metric will be created on'),
    })
    .optional();

export type CustomMetricSchemaType = z.infer<typeof customMetricSchema>;

export const customSqlDimensionSchema = z.object({
    name: z.string().describe('Name of the dimension'),
    description: z.string().describe('Description of the dimension'),
    label: z.string().describe('Label of the dimension'),
    table: z.string().describe('Name of the table this dimension is based on'),
    sql: z.string().describe('SQL query to create the dimension'),
    dimensionType: z
        .nativeEnum(DimensionType)
        .describe('Type of the dimension'),
});

export type CustomSqlDimensionSchemaType = z.infer<
    typeof customSqlDimensionSchema
>;

export const customSqlTraitSchema = z
    .object({
        sql: z
            .string()
            .describe('Complete SQL query to generate the aggregated value'),
        name: z.string().describe('Name of the custom SQL trait (snake_case)'),
        label: z.string().describe('Human-readable label for the trait'),
        description: z
            .string()
            .describe('Description of what the trait calculates'),
        dimensions: z
            .array(
                z.object({
                    reference: z.string(),
                    type: z.nativeEnum(DimensionType),
                }),
            )
            .describe(
                'All columns selected in the SQL query - must include both grouping column and calculated result column',
            ),
        table: z
            .string()
            .describe(
                'Name of the source table where the custom attribute will be created',
            ),
        join: z
            .object({
                srcTableJoinColumn: z
                    .string()
                    .describe(
                        'Column in source table for joining (usually the primary key)',
                    ),
                joinColumn: z
                    .string()
                    .describe(
                        'Column from the SQL query output that corresponds to srcTableJoinColumn for joining (usually the grouping column)',
                    ),
                joinColumnFieldType: z
                    .nativeEnum(DimensionType)
                    .describe(
                        'Type of the join column - must match the type of the grouping column used for joining',
                    ),
                type: z
                    .nativeEnum(JoinType)
                    .describe('Type of join relationship'),
            })
            .describe('Join configuration for the trait'),
        outputColumn: z
            .string()
            .describe(
                'Name of the output column containing the aggregated result',
            ),
    })
    .describe('Custom SQL trait definition');

export type CustomSqlTraitSchemaType = z.infer<typeof customSqlTraitSchema>;
